import React from 'react';
import {
  Title,
  Text,
  Group,
  Stack,
  Card,
  Select,
  Divider,
  Tabs,
  useMantineColorScheme,
} from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import { IconBell, IconPalette, IconTags } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useAnalytics, AnalyticsEvents } from '../../analytics';
import { TagsManagementPanel } from '../../components/settings/TagsManagementPanel';
import { NotificationsSection } from '../../components/settings/NotificationsSection';
import { usePushSettings } from '../../hooks/usePushApi';
import { usePermission } from '../../hooks/usePermission';

export const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const { track } = useAnalytics();
  // Теги — справочник структуры и карт: company.structure или maps.manage, как на бэкенде.
  const canEditStructure = usePermission('company.structure');
  const canEditMaps = usePermission('maps.manage');
  const canManageTags = canEditStructure || canEditMaps;
  // Пуши не настроены на сервере — вкладки нет, иначе она пустая.
  const isPushConfigured = usePushSettings().data?.isConfigured === true;
  const language = i18n.resolvedLanguage === 'en' ? 'en' : 'ru';

  const handleThemeChange = (value: string | null) => {
    if (!value) return;
    setColorScheme(value as 'light' | 'dark' | 'auto');
    track(AnalyticsEvents.SETTINGS_CHANGED, { section: 'theme' });
  };

  const handleLanguageChange = (value: string | null) => {
    if (!value) return;
    i18n.changeLanguage(value);
    track(AnalyticsEvents.SETTINGS_CHANGED, { section: 'language' });
  };

  return (
    <PageContainer>
      {/* Заголовок */}
      <Group gap="md" align="center" mb={{ base: 'md', sm: 'xl' }}>
        <Title order={2}>{t('settings.title')}</Title>
      </Group>

      <Stack gap="lg" style={{ maxWidth: '900px' }}>
        {/* Основные настройки */}
        <Tabs defaultValue="interface">
          <Tabs.List>
            <Tabs.Tab value="interface" leftSection={<IconPalette size={16} />}>
              {t('settings.interface')}
            </Tabs.Tab>
            {isPushConfigured && (
              <Tabs.Tab value="notifications" leftSection={<IconBell size={16} />}>
                {t('settings.notifications')}
              </Tabs.Tab>
            )}
            {canManageTags && (
              <Tabs.Tab value="tags" leftSection={<IconTags size={16} />}>
                {t('tagsManagement.title')}
              </Tabs.Tab>
            )}
          </Tabs.List>

          {isPushConfigured && (
            <Tabs.Panel value="notifications" pt="md">
              <NotificationsSection />
            </Tabs.Panel>
          )}

          <Tabs.Panel value="interface" pt="md">
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Stack gap="lg">
                <div>
                  <Title order={3} mb="xs">
                    {t('settings.interfaceSettings')}
                  </Title>
                  <Text c="dimmed" size="sm">
                    {t('settings.interfaceDescription')}
                  </Text>
                </div>

                <Divider />

                <Stack gap="md">
                  <Select
                    label={t('settings.themeLabel')}
                    value={colorScheme}
                    onChange={handleThemeChange}
                    data={[
                      { value: 'light', label: t('settings.light') },
                      { value: 'dark', label: t('settings.dark') },
                      { value: 'auto', label: t('settings.auto') },
                    ]}
                  />

                  <Select
                    label={t('settings.languageLabel')}
                    value={language}
                    onChange={handleLanguageChange}
                    data={[
                      { value: 'ru', label: t('settings.russian') },
                      { value: 'en', label: t('settings.english') },
                    ]}
                  />
                </Stack>
              </Stack>
            </Card>
          </Tabs.Panel>

          {canManageTags && (
            <Tabs.Panel value="tags" pt="md">
              <TagsManagementPanel />
            </Tabs.Panel>
          )}
        </Tabs>
      </Stack>
    </PageContainer>
  );
};
